export const UK_TIMEZONE = 'Europe/London';

type DateInput = string | Date | null | undefined;

function toDate(value: DateInput): Date | null {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(String(value));
  return Number.isNaN(date.getTime()) ? null : date;
}

function getUkDateTimeParts(date: Date) {
  const formatter = new Intl.DateTimeFormat('en-GB', {
    timeZone: UK_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  });
  const parts = formatter.formatToParts(date);
  const get = (type: Intl.DateTimeFormatPartTypes) =>
    Number(parts.find((part) => part.type === type)?.value ?? '0');
  return {
    year: get('year'),
    month: get('month'),
    day: get('day'),
    hour: get('hour'),
    minute: get('minute'),
  };
}

/** Format a timestamp for dashboard display in UK time only. */
export function formatUkDateTime(
  value: DateInput,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = toDate(value);
  if (!date) return '—';

  return date.toLocaleString('en-GB', {
    timeZone: UK_TIMEZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    ...options,
  });
}

/** Format a date (no time) for dashboard display in UK time only. */
export function formatUkDate(
  value: DateInput,
  options?: Intl.DateTimeFormatOptions,
): string {
  const date = toDate(value);
  if (!date) return '—';

  return date.toLocaleDateString('en-GB', {
    timeZone: UK_TIMEZONE,
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    ...options,
  });
}

/** Pickup-style datetime with weekday, always in UK time. */
export function formatUkPickupDateTime(value: DateInput): string {
  return formatUkDateTime(value, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
}

/** Value for `<input type="datetime-local">` interpreted as UK local time. */
export function toUkDatetimeLocalValue(value: unknown): string {
  if (!value) return '';
  const date = value instanceof Date ? value : new Date(String(value));
  if (Number.isNaN(date.getTime())) return '';

  const parts = getUkDateTimeParts(date);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${parts.year}-${pad(parts.month)}-${pad(parts.day)}T${pad(parts.hour)}:${pad(parts.minute)}`;
}

/** Parse a UK `datetime-local` value back to ISO UTC. */
export function ukDatetimeLocalToIso(localValue: string): string {
  if (!localValue) return '';

  const match = localValue.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})$/);
  if (!match) {
    const fallback = new Date(localValue);
    return Number.isNaN(fallback.getTime()) ? '' : fallback.toISOString();
  }

  const [, year, month, day, hour, minute] = match;
  const target = {
    year: Number(year),
    month: Number(month),
    day: Number(day),
    hour: Number(hour),
    minute: Number(minute),
  };

  let utcMs = Date.UTC(
    target.year,
    target.month - 1,
    target.day,
    target.hour,
    target.minute,
  );

  for (let attempt = 0; attempt < 3; attempt += 1) {
    const parts = getUkDateTimeParts(new Date(utcMs));
    const diffMinutes =
      (target.year - parts.year) * 525_600 +
      (target.month - parts.month) * 43_200 +
      (target.day - parts.day) * 1_440 +
      (target.hour - parts.hour) * 60 +
      (target.minute - parts.minute);

    if (diffMinutes === 0) break;
    utcMs += diffMinutes * 60 * 1000;
  }

  return new Date(utcMs).toISOString();
}

/** Format a UTC instant as HH:mm in UK local time. */
export function formatUkTimeHHmm(value: DateInput): string {
  const date = toDate(value);
  if (!date) return '';

  const parts = getUkDateTimeParts(date);
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${pad(parts.hour)}:${pad(parts.minute)}`;
}

/** Keep legacy pickup mirror fields aligned with the canonical pickup instant. */
export function syncPickupDatetimeMirrorFields(iso: string): {
  pickupTime: string;
  pickupDateWithTime: string;
  pickupDateTimeStr: string;
  pickupTimeSpan: string;
  pickupTimeString: string;
} {
  const timeSpan = formatUkTimeHHmm(iso);
  return {
    pickupTime: iso,
    pickupDateWithTime: iso,
    pickupDateTimeStr: iso,
    pickupTimeSpan: timeSpan,
    pickupTimeString: timeSpan,
  };
}

export const formatDate = (date: Date, format: string): string => {
  const pad = (n: number) => n.toString().padStart(2, '0');

  const yyyy = date.getFullYear();
  const MM = pad(date.getMonth() + 1);
  const dd = pad(date.getDate());
  const HH = pad(date.getHours());
  const mm = pad(date.getMinutes());
  const ss = pad(date.getSeconds());

  return format
    .replace('yyyy', yyyy.toString())
    .replace('MM', MM)
    .replace('dd', dd)
    .replace('HH', HH)
    .replace('mm', mm)
    .replace('ss', ss);
};

export const getFileExtension = (filename: string): string => {
  return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
};

export const formatDateForFileName = (date: Date): string => {
  return date.toISOString()
    .replace(/[:.]/g, '-')
    .replace('T', '_')
    .slice(0, -5); // Remove milliseconds and timezone
};
